import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import ListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { useTranslation } from "react-i18next";

import { FormatH2, FormatH3, FormatH4 } from "../icons";
import { HtmlTooltip } from "./HtmlTooltip";

export function RichTextToolbar({
    editor,
    disabled,
    onLinkClick,
}: {
    editor: Editor;
    disabled?: boolean;
    onLinkClick: () => void;
}) {
    const { t } = useTranslation();
    const theme = useTheme();

    const editorState = useEditorState({
        editor,
        // This function will be called every time the editor state changes
        selector: ({ editor }: { editor: Editor }) => ({
            isHeading2: editor.isActive("heading", { level: 2 }),
            isHeading3: editor.isActive("heading", { level: 3 }),
            isHeading4: editor.isActive("heading", { level: 4 }),
            isBold: editor.isActive("bold"),
            isItalic: editor.isActive("italic"),
            isUList: editor.isActive("bulletList"),
            isOList: editor.isActive("orderedList"),
            isLink: editor.isActive("link"),
            hasSelection: !editor.state.selection.empty,
        }),
    });
    if (disabled) return null;

    return (
        <Box
            sx={{
                display: "flex",
                gap: 0,
                alignItems: "center",
                px: 0.5,
                py: 0.5,
                borderBottom: 1,
                borderColor: "divider",
            }}
        >
            <ToggleButtonGroup
                size="small"
                exclusive
                sx={{
                    "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: 0,
                        padding: "4px 8px",
                        color: "action.active",
                        backgroundColor: "transparent",
                        transition: theme.transitions.create(
                            ["background-color", "color"],
                            {
                                duration: theme.transitions.duration.shortest,
                            },
                        ),
                        "&:hover": {
                            backgroundColor: alpha(
                                theme.palette.action.hover,
                                0.04,
                            ),
                        },
                        "&.Mui-selected": {
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12,
                            ),
                            color: "primary.main",
                            "&:hover": {
                                backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.16,
                                ),
                            },
                        },
                    },
                    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                        marginLeft: 0,
                        borderLeft: "1px solid",
                        borderColor: alpha(theme.palette.divider, 0.5),
                    },
                    "& .MuiToggleButtonGroup-grouped:first-of-type": {
                        borderRadius: 0,
                    },
                    "& .MuiToggleButtonGroup-grouped:last-of-type": {
                        borderRadius: 0,
                    },
                }}
            >
                <HtmlTooltip
                    title={
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Typography variant="subtitle2">
                                {t("form.field-types.rich-text.heading2")}
                            </Typography>
                            <Typography variant="body2">##</Typography>
                        </Box>
                    }
                >
                    <ToggleButton
                        value="h2"
                        selected={editorState.isHeading2}
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleHeading({ level: 2 })
                                .run()
                        }
                        aria-label={t("form.field-types.rich-text.heading2")}
                    >
                        <FormatH2 fontSize="small" />
                    </ToggleButton>
                </HtmlTooltip>
                <HtmlTooltip
                    title={
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Typography variant="subtitle2">
                                {t("form.field-types.rich-text.heading3")}
                            </Typography>
                            <Typography variant="body2">###</Typography>
                        </Box>
                    }
                >
                    <ToggleButton
                        value="h3"
                        selected={editorState.isHeading3}
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleHeading({ level: 3 })
                                .run()
                        }
                        aria-label={t("form.field-types.rich-text.heading3")}
                    >
                        <FormatH3 fontSize="small" />
                    </ToggleButton>
                </HtmlTooltip>
                <HtmlTooltip
                    title={
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Typography variant="subtitle2">
                                {t("form.field-types.rich-text.heading4")}
                            </Typography>
                            <Typography variant="body2">####</Typography>
                        </Box>
                    }
                >
                    <ToggleButton
                        value="h4"
                        selected={editorState.isHeading4}
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleHeading({ level: 4 })
                                .run()
                        }
                        aria-label={t("form.field-types.rich-text.heading4")}
                    >
                        <FormatH4 fontSize="small" />
                    </ToggleButton>
                </HtmlTooltip>
            </ToggleButtonGroup>

            <ToggleButtonGroup
                size="small"
                exclusive
                sx={{
                    "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: 0,
                        padding: "4px 8px",
                        color: "action.active",
                        backgroundColor: "transparent",
                        transition: theme.transitions.create(
                            ["background-color", "color"],
                            {
                                duration: theme.transitions.duration.shortest,
                            },
                        ),
                        "&:hover": {
                            backgroundColor: alpha(
                                theme.palette.action.hover,
                                0.04,
                            ),
                        },
                        "&.Mui-selected": {
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12,
                            ),
                            color: "primary.main",
                            "&:hover": {
                                backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.16,
                                ),
                            },
                        },
                    },
                    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                        marginLeft: 0,
                        borderLeft: "1px solid",
                        borderColor: alpha(theme.palette.divider, 0.5),
                    },
                }}
            >
                <ToggleButton
                    value="bold"
                    selected={editorState.isBold}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    aria-label={t("form.field-types.rich-text.bold")}
                >
                    <FormatBoldIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton
                    value="italic"
                    selected={editorState.isItalic}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    aria-label={t("form.field-types.rich-text.italic")}
                >
                    <FormatItalicIcon fontSize="small" />
                </ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
                size="small"
                exclusive
                sx={{
                    "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: 0,
                        padding: "4px 8px",
                        color: "action.active",
                        backgroundColor: "transparent",
                        transition: theme.transitions.create(
                            ["background-color", "color"],
                            {
                                duration: theme.transitions.duration.shortest,
                            },
                        ),
                        "&:hover": {
                            backgroundColor: alpha(
                                theme.palette.action.hover,
                                0.04,
                            ),
                        },
                        "&.Mui-selected": {
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12,
                            ),
                            color: "primary.main",
                            "&:hover": {
                                backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.16,
                                ),
                            },
                        },
                        "&.Mui-disabled": {
                            color: alpha(theme.palette.action.active, 0.38),
                        },
                    },
                    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                        marginLeft: 0,
                        borderLeft: "1px solid",
                        borderColor: alpha(theme.palette.divider, 0.5),
                    },
                }}
            >
                <HtmlTooltip
                    title={
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Typography variant="subtitle2">
                                {t("form.field-types.rich-text.bullet-list")}
                            </Typography>
                            <Typography variant="body2">-</Typography>
                        </Box>
                    }
                >
                    <ToggleButton
                        value="bulletList"
                        selected={editorState.isUList}
                        onClick={() =>
                            editor.chain().focus().toggleBulletList().run()
                        }
                        aria-label={t("form.field-types.rich-text.bullet-list")}
                    >
                        <ListIcon fontSize="small" />
                    </ToggleButton>
                </HtmlTooltip>
                <HtmlTooltip
                    title={
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Typography variant="subtitle2">
                                {t("form.field-types.rich-text.numbered-list")}
                            </Typography>
                            <Typography variant="body2">1.</Typography>
                        </Box>
                    }
                >
                    <ToggleButton
                        value="orderedList"
                        selected={editorState.isOList}
                        onClick={() =>
                            editor.chain().focus().toggleOrderedList().run()
                        }
                        aria-label={t(
                            "form.field-types.rich-text.numbered-list",
                        )}
                    >
                        <ListNumberedIcon fontSize="small" />
                    </ToggleButton>
                </HtmlTooltip>
                <ToggleButton
                    value="link"
                    disabled={!editorState.hasSelection && !editorState.isLink}
                    selected={editorState.isLink}
                    onClick={onLinkClick}
                    aria-label={t("form.field-types.rich-text.insert-link")}
                >
                    <LinkIcon fontSize="small" />
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );
}
